import { observable, action } from "mobx";
import { makeApi, URLS } from "Utils/apiURL";

class ApprovalPopupState {
  @observable showPopup = false;
  @observable sequenceId = null;
  @observable contacts = [];           // list of contacts awaiting approval (from /awaiting-approval-contacts)
  @observable currentIndex = 0;        // pointer into contacts[] — for prev/next
  @observable messages = [];           // drs_task rows for the current contact
  @observable loadingContacts = false;
  @observable loadingMessages = false;
  @observable saving = false;
  @observable showResearchSummary = false;
  @observable researchSummary = null;  // contact_deep_research_overview payload
  @observable regeneratingAll = false; // true while the whole-arc regen is in flight

  @action
  open = (sequenceId, contactIdToFocus = null) => {
    this.showPopup = true;
    this.sequenceId = sequenceId;
    this.currentIndex = 0;
    this.contacts = [];
    this.messages = [];
    this.researchSummary = null;
    this.showResearchSummary = false;
    this.loadContacts(contactIdToFocus);
  };

  @action
  close = () => {
    this.showPopup = false;
    this.sequenceId = null;
    this.contacts = [];
    this.messages = [];
    this.researchSummary = null;
    this.showResearchSummary = false;
    this.currentIndex = 0;
  };

  loadContacts = (contactIdToFocus = null) => {
    if (!this.sequenceId) return;
    this.loadingContacts = true;
    makeApi({
      url: `${URLS.drsSequenceAwaitingApprovalContacts.replace("{sequenceId}", this.sequenceId)}`,
      method: "GET"
    })
      .then(res => {
        this._applyContacts(res?.data?.contacts || [], contactIdToFocus);
      })
      .catch(() => {
        this._applyContacts([], contactIdToFocus);
      });
  };

  @action
  _applyContacts = (list, contactIdToFocus) => {
    this.contacts = list || [];
    this.loadingContacts = false;
    if (this.contacts.length === 0) {
      this.messages = [];
      return;
    }
    if (contactIdToFocus) {
      const idx = this.contacts.findIndex(c => c.contactId === contactIdToFocus);
      this.currentIndex = idx >= 0 ? idx : 0;
    } else {
      this.currentIndex = 0;
    }
    this.loadMessagesForCurrent();
  };

  loadMessagesForCurrent = () => {
    const contact = this.contacts[this.currentIndex];
    if (!contact || !this.sequenceId) return;
    this.loadingMessages = true;
    makeApi({
      url: URLS.drsSequenceContactMessages
        .replace("{sequenceId}", this.sequenceId)
        .replace("{contactId}", contact.contactId),
      method: "GET"
    })
      .then(res => {
        this._applyMessages(res?.data?.messages || []);
      })
      .catch(() => {
        this._applyMessages([]);
      });
  };

  @action
  _applyMessages = (list) => {
    this.messages = list || [];
    this.loadingMessages = false;
  };

  @action
  goPrev = () => {
    if (this.currentIndex > 0) {
      this.currentIndex -= 1;
      this.researchSummary = null;
      this.loadMessagesForCurrent();
    }
  };

  @action
  goNext = () => {
    if (this.currentIndex < this.contacts.length - 1) {
      this.currentIndex += 1;
      this.researchSummary = null;
      this.loadMessagesForCurrent();
    }
  };

  @action
  toggleResearchSummary = () => {
    this.showResearchSummary = !this.showResearchSummary;
    if (this.showResearchSummary && !this.researchSummary) {
      const contact = this.contacts[this.currentIndex];
      if (!contact) return;
      makeApi({
        url: `${URLS.deepResearchOverview}/${contact.contactId}`,
        method: "GET"
      }).then(res => {
        this.researchSummary = res?.data || {};
      }).catch(() => {
        this.researchSummary = {};
      });
    }
  };

  approveCurrentContact = () => {
    const contact = this.contacts[this.currentIndex];
    if (!contact || !this.sequenceId) return;
    this.saving = true;
    return makeApi({
      url: URLS.drsSequenceContactApprove
        .replace("{sequenceId}", this.sequenceId)
        .replace("{contactId}", contact.contactId),
      method: "POST",
      data: {}
    }).then(() => {
      this._afterContactDecision();
    }).catch(() => {
      this.saving = false;
    });
  };

  // Caller is responsible for confirming via the in-app ConfirmationPopup —
  // never use window.confirm (bad UX, doesn't match the rest of the product).
  rejectCurrentContact = () => {
    const contact = this.contacts[this.currentIndex];
    if (!contact || !this.sequenceId) return;
    this.saving = true;
    return makeApi({
      url: URLS.drsSequenceContactReject
        .replace("{sequenceId}", this.sequenceId)
        .replace("{contactId}", contact.contactId),
      method: "POST",
      data: {}
    }).then(() => {
      this._afterContactDecision();
    }).catch(() => {
      this.saving = false;
    });
  };

  @action
  _afterContactDecision = () => {
    this.saving = false;
    // Remove the decided contact from the list
    this.contacts = this.contacts.filter((_, i) => i !== this.currentIndex);
    if (this.contacts.length === 0) {
      this.close();
      return;
    }
    if (this.currentIndex >= this.contacts.length) {
      this.currentIndex = this.contacts.length - 1;
    }
    this.loadMessagesForCurrent();
  };

  editMessage = (drsTaskId, body) => {
    return makeApi({
      url: URLS.drsTaskEdit.replace("{id}", drsTaskId),
      method: "POST",
      data: body
    }).then(() => {
      // re-fetch to pick up server-side state (version bump etc.)
      this.loadMessagesForCurrent();
    });
  };

  regenerateMessage = (drsTaskId, instruction) => {
    return makeApi({
      url: URLS.drsTaskRegenerate.replace("{id}", drsTaskId),
      method: "POST",
      data: instruction ? { instruction } : {}
    }).then(() => {
      this.loadMessagesForCurrent();
    });
  };

  /**
   * Regenerate every message for the current contact in one LLM call so the
   * arc threads coherently. Per-step regen drifts; this preserves narrative.
   */
  regenerateAllForCurrentContact = () => {
    const contact = this.contacts[this.currentIndex];
    if (!contact || !this.sequenceId) return;
    this.saving = true;
    this.regeneratingAll = true;
    return makeApi({
      url: URLS.drsSequenceContactRegenerateAll
        .replace("{sequenceId}", this.sequenceId)
        .replace("{contactId}", contact.contactId),
      method: "POST",
      data: {}
    })
      .then(() => {
        this.loadMessagesForCurrent();
      })
      .finally(() => {
        this.saving = false;
        this.regeneratingAll = false;
      });
  };

  cancelScheduled = (drsTaskId) => {
    return makeApi({
      url: URLS.drsTaskCancel.replace("{id}", drsTaskId),
      method: "POST",
      data: {}
    }).then(() => {
      this.loadMessagesForCurrent();
    });
  };
}

const approvalPopupState = new ApprovalPopupState();
export { approvalPopupState };
export default approvalPopupState;
